import {
  LoadingManager,
  Mesh,
  TextureLoader,
  Object3D,
  RepeatWrapping,
  Box3,
  BufferGeometry,
  NearestFilter,
  PlaneGeometry,
  MeshLambertMaterial,
  DoubleSide,
  Color,
  Light,
  SRGBColorSpace,
  Vector3,
} from "three";
import { CSM } from "three/examples/jsm/csm/CSM.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";

import { DEFAULT_ASSET_BASE } from "../../constants";
import { Context } from "../../context";
import { Collider } from "./collider";
import {
  teamAbbrevToId,
  venueIdToAbbrev,
  teamAbbrevToVenueId,
  teamIdToAbbrev,
} from "../../../lib/types/statsApi";
import { Crowd } from "./crowd";
import { PoserClock } from "../../util/clock";
import { defaultScale } from "../../constants";
import { UserOptions } from "../../util/user-options";

import { StadiumGroundFallback } from "./ground";
import { Jumbotron } from "./jumbotron";

const DEFAULT_STADIUM_URL = `${DEFAULT_ASSET_BASE}/models/ballparks/Stadium_LAD.glb`;

// load universal basis gpu compressed textures
const texLoader = new KTX2Loader();
const dracoLoader = new DRACOLoader();

const loadManager = new LoadingManager();
loadManager.addHandler(/\.ktx2$/, texLoader);

export const stadiumGltfLoader = new GLTFLoader(loadManager);
stadiumGltfLoader.setKTX2Loader(texLoader);
stadiumGltfLoader.setDRACOLoader(dracoLoader);

const textureLoader = new TextureLoader();

export class Stadium extends Object3D {
  fallback = new StadiumGroundFallback();

  collider?: Collider;
  box?: Box3;
  loaded: boolean = false;

  crowd?: Crowd;

  jumbotron: Jumbotron;

  private basePath: string | undefined = undefined;

  private contextRef: Context;

  constructor(context: Context) {
    super();

    this.contextRef = context;
    this.jumbotron = new Jumbotron(context);

    this.add(this.jumbotron);
  }

  /**
   *
   * @param context
   * @param venueAbbr
   * @param path used for drop-in - specifies local file
   * @returns
   */
  async init(data?: string) {
    const { options, gameState } = this.contextRef;
    const assetBase = options.localBallparks
      ? options.assetBase || DEFAULT_ASSET_BASE
      : DEFAULT_ASSET_BASE;

    this.basePath = `${assetBase}/models/ballparks`;

    dracoLoader.setDecoderPath(`${assetBase}/libs/draco/`);

    this.fallback.visible = true;
    this.add(this.fallback);

    let gltf: GLTF;
    let path: string | undefined = undefined;

    const homeId = options.homeOverride
      ? teamAbbrevToId[options.homeOverride]
      : gameState.getTeams()?.home.team.id || 0;
    const awayId = options.awayOverride
      ? teamAbbrevToId[options.awayOverride]
      : gameState.getTeams()?.away.team.id || 0;

    const homeAbbr = options.homeOverride ?? teamIdToAbbrev[homeId];
    const awayAbbr = options.awayOverride ?? teamIdToAbbrev[awayId];

    const venueId =
      options.venueOverride !== undefined
        ? teamAbbrevToVenueId[options.venueOverride]
        : gameState.getVenueId();

    const venueAbbr = venueId !== undefined ? venueIdToAbbrev[venueId] : "LAD";

    if (data) {
      gltf = await stadiumGltfLoader.loadAsync(data);
    } else {
      path = `${this.basePath}/Stadium_${venueAbbr}.glb`;

      const headResp = await fetch(path, { method: "HEAD" });

      path = headResp.status !== 200 ? this.getFallback() : path;

      gltf = await stadiumGltfLoader.loadAsync(path);
    }

    this.processGLTF(gltf, venueAbbr, homeAbbr, awayAbbr);
    console.log("Done loading stadium from ", path ?? "drop-in");

    this.addGround();

    if (!this.collider) {
      console.error("no collider for stadium ", path ?? "drop-in");
    }
    this.loaded = true;

    this.jumbotron.init(assetBase, venueAbbr);

    document.addEventListener("dragover", this.dragOverCb);
    document.addEventListener("dragenter", this.dragEnterCb);
    document.addEventListener("dragleave", this.dragLeaveCb);
    document.addEventListener("drop", this.dropCb);
  }

  teardown() {
    this.jumbotron.teardown();

    document.removeEventListener("dragover", this.dragOverCb);
    document.removeEventListener("dragenter", this.dragEnterCb);
    document.removeEventListener("dragleave", this.dragLeaveCb);
    document.removeEventListener("drop", this.dropCb);
  }

  update(clock: PoserClock) {
    this.crowd?.update(clock);
    this.jumbotron.update();
  }

  private processGLTF(
    gltf: GLTF,
    venueAbbr: string,
    homeAbbr: string,
    awayAbbr: string
  ) {
    const stadium = gltf.scene;
    this.box?.setFromObject(stadium);

    this.add(stadium);

    this.addCrowd(stadium, venueAbbr, homeAbbr, awayAbbr);

    const lights: Object3D[] = [];

    const shadows = this.contextRef.userOptions.hasShadows();

    const matNames: string[] = [];
    let foundAdMaster = false;
    stadium.traverse((obj: Object3D) => {
      if (obj instanceof Light) {
        lights.push(obj);
      }
      if (obj.name == "CameraCollider") {
        obj.receiveShadow = false;
        obj.castShadow = false;
      } else if (obj instanceof Mesh) {
        if (obj.material.name.toLowerCase().includes("field")) {
          obj.receiveShadow = shadows;
          // turn off field shadow casting to reduce shadow acne
          // obj.castShadow = shadows;
        } else {
          obj.receiveShadow = shadows;
          obj.castShadow = shadows;
        }

        const matType = this.contextRef.userOptions.getMaterialCtor().stadium;
        const old = obj.material;
        const material = new matType();
        material.copy(obj.material);
        obj.material = material;
        old.dispose();

        if (obj.material.name.toLowerCase().includes("field")) {
          obj.material.roughness = 1;
        }

        matNames.push(obj.material);
        if (obj.material.name.includes("AdMaster")) {
          const tex = textureLoader.load(`${this.basePath}/Billboards.jpg`);
          tex.colorSpace = SRGBColorSpace;
          tex.flipY = false;

          tex.wrapS = RepeatWrapping;

          obj.material.map = tex;
          foundAdMaster = true;
        }

        const sun = this.contextRef.world?.lights.sky?.sun;
        if (sun instanceof CSM) {
          sun.setupMaterial(obj.material);
        }
      }
    });

    if (!foundAdMaster) {
      console.warn("no admaster found", matNames);
    }

    // TODO: add lights into stadiums at correct locations - for night games
    // remove lights from TB
    for (const light of lights) {
      light.removeFromParent();
    }

    stadium.scale.set(defaultScale, defaultScale, defaultScale);
    stadium.visible = this.contextRef.state.stadiumEnabled;

    this.collider = Collider.fromGLTF(gltf);

    this.fallback.visible = false;
  }

  private addGround() {
    const { options, renderer } = this.contextRef;
    const assetBase = options.assetBase;

    const maxAnsiotropy = renderer.capabilities.getMaxAnisotropy();

    const groundTex = new TextureLoader().load(`${assetBase}/images/grass.png`);
    groundTex.anisotropy = maxAnsiotropy;
    groundTex.magFilter = NearestFilter;
    groundTex.premultiplyAlpha = true;
    groundTex.wrapS = RepeatWrapping;
    groundTex.wrapT = RepeatWrapping;
    groundTex.repeat.set(16, 16);

    const ground = new Mesh(
      new PlaneGeometry(10000, 10000),
      new MeshLambertMaterial({
        map: groundTex,
        side: DoubleSide,
        color: new Color(0x808080),
      })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = false;
    ground.castShadow = true;

    ground.position.y = -10;

    this.add(ground);
  }

  private async addCrowd(
    lookIn: Object3D,
    venueAbbr: string,
    homeAbbr: string,
    awayAbbr: string,
    separate = false
  ) {
    const crowdGeom = lookIn.getObjectByName("Crowd");

    if (
      crowdGeom &&
      crowdGeom instanceof Mesh &&
      crowdGeom.geometry instanceof BufferGeometry
    ) {
      if (separate) {
        crowdGeom.scale.set(defaultScale, defaultScale, defaultScale);
      }

      this.crowd = await Crowd.make(
        this.contextRef,
        crowdGeom,
        homeAbbr,
        awayAbbr
      );
      this.add(this.crowd);
    } else if (!separate) {
      const crowdGltf = await this.fetchCrowd(venueAbbr);
      if (crowdGltf) {
        this.addCrowd(crowdGltf.scene, venueAbbr, homeAbbr, awayAbbr, true);
      }
    } else {
      console.error("unable to get crowd geometry");
    }
  }

  private async fetchCrowd(homeAbbr: string): Promise<GLTF | undefined> {
    // fetch separate crowd file:
    const crowdPath = `${this.basePath}/crowd/Stadium_${homeAbbr}-crowd.glb`;

    const headResp = await fetch(crowdPath, { method: "HEAD" });
    if (headResp.status !== 200) {
      console.error("can't fetch separate crowd geometry");
      return undefined;
    }

    return await stadiumGltfLoader.loadAsync(crowdPath);
  }

  private getFallback(): string {
    console.warn("Using fallback stadium:", DEFAULT_STADIUM_URL);
    return DEFAULT_STADIUM_URL;
  }

  // drag-drop stadium handlers
  private dragOverCb = (event: DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  };

  private dragEnterCb = () => {
    document.body.style.opacity = "0.5";
  };

  private dragLeaveCb = () => {
    document.body.style.opacity = "1";
  };

  private dropCb = (event: DragEvent) => {
    event.preventDefault();

    const reader = new FileReader();
    reader.addEventListener("load", (loadEvent) => {
      if (loadEvent.target?.result) {
        const dataUrl = loadEvent.target.result as string;
        const mimeType = dataUrl.split(",")[0].split(":")[1].split(";")[0];

        const path = loadEvent.target.result as string;
        this.init(path);
      }
    });

    if (event.dataTransfer) {
      reader.readAsDataURL(event.dataTransfer.files[0]);
    }
    document.body.style.opacity = "1";
  };
}
